<template>
  <div class="page requests">
    <ErrorAlert :message="errorMessage" @clear-error="clearMessage" />
    <SuccessAlert :message="successMessage" @clear-error="clearMessage" />

    <PageHeader title="Demersuri" :subtitle="subtitle" icon="mdi-file-document-edit-outline">
      <template #actions>
        <v-btn
          v-if="isEngineer || isAdmin || isContability || isDtiSef"
          class="btn-ghost-light"
          @click="showDateRangePicker = true"
        >
          <v-icon left>mdi-microsoft-excel</v-icon>Raport Excel
        </v-btn>
        <v-btn class="btn-leaf" @click="$refs.dialogForm.open()">
          <v-icon left>mdi-plus</v-icon>Demers nou
        </v-btn>
      </template>
      <DepartmentFilter
        v-model="department"
        :items="requestsData"
        field="userDepartment"
        :departments="requestDepartments"
      />
    </PageHeader>

    <div class="page-body">
      <section class="panel">
        <div class="panel-toolbar">
          <h2 class="panel-title">
            {{ department || "Toate demersurile" }}
            <span class="count">{{ filteredData.length }}</span>
          </h2>
          <span class="grow-spacer" />
          <SearchBar @search="handleSearch" />
          <ColumnSelect :headers="headers" :updateVisibleColumns="updateVisibleColumns" pref-key="requests" />
        </div>
        <v-data-table
          :mobile-breakpoint="0"
          :headers="visibleHeaders"
          :items="paginatedItems"
          :sort-by="pagination.sortBy"
          :sort-desc="pagination.sortDesc"
          :loading="isLoading"
          loading-text="Se încarcă demersurile..."
          no-data-text="Nu există demersuri"
          :options.sync="pagination"
          :server-items-length="filteredData.length"
          @update:options="handleTableOptionsUpdate"
          :multi-sort="false"
          :must-sort="true"
          sortable
        >
          <template v-slot:item="{ item, index }">
            <tr
              :class="{
                'light-green-row': index % 2 !== 0,
                'row-rejected': item.status === 'Respins',
                'row-cancelled': item.statusId === CANCELLED,
              }"
            >
              <td
                v-for="(header, hIndex) in visibleHeaders"
                :key="hIndex"
                :style="header.style ? header.style : {}"
              >
                <template v-if="header.value === 'comments'">
                  <div v-if="item.commentsHistory && item.commentsHistory.length" class="comments-wrapper">
                    <v-timeline dense class="compact-timeline">
                      <v-timeline-item
                        v-for="(comment, cIndex) in item.commentsHistory"
                        :key="cIndex"
                        small
                        :color="statusColor(comment.status)"
                        class="compact-item"
                      >
                        <div class="comment-header">
                          <strong class="user-name">{{ comment.user }}</strong>
                          <StatusChip :status="comment.status" small class="ml-2" />
                        </div>
                        <div v-if="comment.comment" class="comment-body">{{ comment.comment }}</div>
                        <PhotoGallery v-if="comment.files && comment.files.length" class="comment-photos" :files="comment.files" :size="44" download-name="demers" />
                        <div class="comment-date">{{ formatDate(comment.date) }}</div>
                      </v-timeline-item>
                    </v-timeline>
                  </div>
                  <div v-else class="no-comments">—</div>
                  <!-- anyone who sees the demer may comment on it, with photos; the stage does not move -->
                  <v-btn
                    v-if="item.statusId !== CANCELLED"
                    small
                    text
                    color="primary"
                    class="add-comment-btn"
                    @click="openAddComment(item)"
                  >
                    <v-icon left small>mdi-comment-plus-outline</v-icon>Adaugă comentariu
                  </v-btn>
                </template>
                <template v-else-if="header.value === 'status'">
                  <StatusChip :status="item.status" />
                  <!-- the author, once, after Contabilitate rejected -->
                  <v-btn
                    v-if="correctable[item.id]"
                    small
                    outlined
                    color="primary"
                    class="correct-btn"
                    :loading="openingCorrection === item.id"
                    @click="openCorrection(item)"
                  >
                    <v-icon left small>mdi-file-edit-outline</v-icon>Corectează și retrimite
                  </v-btn>
                </template>
                <template v-else-if="header.value === 'actions'">
                  <!-- an icon, like the other tables: a text button pushed the column off a 1400px screen -->
                  <v-btn
                    v-if="isAdmin && item.statusId !== CANCELLED && item.statusId !== DONE"
                    icon
                    color="error"
                    aria-label="Anulează demersul"
                    title="Anulează demersul"
                    @click="cancelItem(item)"
                  >
                    <v-icon>mdi-cancel</v-icon>
                  </v-btn>
                </template>
                <template v-else>
                  {{ getTableCellValue(item, header, index) }}
                </template>
              </td>
            </tr>
          </template>
        </v-data-table>
      </section>
    </div>

    <v-dialog v-model="showDateRangePicker" persistent max-width="440">
      <v-card>
        <v-card-title>
          Raport pentru o perioadă
          <v-spacer></v-spacer>
          <v-btn icon aria-label="Închide" @click="closeDateRangePicker">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p class="report-hint">Demersurile îndeplinite între cele două date, ca fișier Excel.</p>
          <v-menu
            ref="startMenu"
            v-model="startMenu"
            :close-on-content-click="false"
            :return-value.sync="startDate"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedStartDate"
                label="De la"
                prepend-inner-icon="mdi-calendar"
                outlined
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker v-model="startDate" no-title scrollable locale="ro" first-day-of-week="1" @change="saveStartDate"></v-date-picker>
          </v-menu>
          <v-menu
            ref="endMenu"
            v-model="endMenu"
            :close-on-content-click="false"
            :return-value.sync="endDate"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedEndDate"
                label="Până la"
                prepend-inner-icon="mdi-calendar"
                outlined
                readonly
                hide-details
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker v-model="endDate" no-title scrollable locale="ro" first-day-of-week="1" @change="saveEndDate"></v-date-picker>
          </v-menu>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDateRangePicker">Anulare</v-btn>
          <v-btn class="btn-green" @click="downloadReport">
            <v-icon left>mdi-download</v-icon>Descarcă raportul
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DialogForm ref="dialogForm" @updateRequestData="fetchRequestData" />
    <CancelRequestDialog
      ref="cancelDialog"
      @updateRequestData="onRequestChanged"
      @success="handleSuccess"
      @error="handleError"
    />
    <CorrectRequestDialog
      ref="correctDialog"
      @updateRequestData="onRequestChanged"
      @success="handleSuccess"
      @error="handleError"
    />
    <AddCommentDialog
      ref="addComment"
      @saved="(saved) => onRequestChanged(saved && saved.request_id)"
      @success="handleSuccess"
      @error="handleError"
    />
  </div>
</template>

<script>
import DialogForm from "./DialogForm.vue";
import CancelRequestDialog from "./CancelRequestDialog.vue";
import CorrectRequestDialog from "./CorrectRequestDialog.vue";
import AddCommentDialog from "@/components/requests/AddCommentDialog.vue";
import PhotoGallery from "@/components/files/PhotoGallery.vue";
import PageHeader from "@/components/layout/PageHeader.vue";
import StatusChip, { statusColor } from "@/components/elements/StatusChip.vue";
import DepartmentFilter from "@/components/elements/DepartmentFilter.vue";
import ColumnSelect from "@/components/elements/ColumnSelect.vue";
import SearchBar from "@/components/elements/SearchBar.vue";
import SuccessAlert from "@/components/alerts/SuccessAlert.vue";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import * as api from "./api";
import * as helper from "@/helper/helper.js";
import { mapGetters } from "vuex";
import { fetchRequestsForRole } from "./scope";
import { STAGES_005 } from "@/config/periferice";

const REJECTED = 4;

export default {
  name: "RequestsPage",
  components: {
    DialogForm,
    CancelRequestDialog,
    CorrectRequestDialog,
    AddCommentDialog,
    PhotoGallery,
    PageHeader,
    StatusChip,
    DepartmentFilter,
    ColumnSelect,
    SearchBar,
    SuccessAlert,
    ErrorAlert,
  },
  data() {
    return {
      headers: [],
      requestsData: [],
      department: "",
      visibleHeaders: [],
      CANCELLED: STAGES_005.CANCELLED,
      // a request that is done cannot be cancelled
      DONE: 9,
      // request id -> may the current user correct it (asked once per rejected request of theirs)
      correctable: {},
      openingCorrection: null,
      search: "",
      sortBy: "id",
      errorMessage: "",
      successMessage: "",
      sortDesc: true,
      acction: false,
      isLoading: true,
      startDate: '',
      endDate: '',
      formattedStartDate: '',
      formattedEndDate: '',
      startMenu: false,
      endMenu: false,
      showDateRangePicker: false,
      commentsCache: {},
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["id"],
        sortDesc: [true]
      },
      commentLoading: false,
      debugMessage: "",
    };
  },
  watch: {
    startDate(newValue) {
      this.formattedStartDate = newValue ? newValue : '';
    },
    endDate(newValue) {
      this.formattedEndDate = newValue ? newValue : '';
    },
    'pagination.page': function() {
      this.fetchCommentsForVisibleItems();
    },
    pagination: {
      handler(newVal, oldVal) {
        if (newVal.page !== oldVal.page || newVal.itemsPerPage !== oldVal.itemsPerPage) {
          this.fetchCommentsForVisibleItems();
        }
      },
      deep: true
    },
    filteredData: {
      handler() {
        this.$nextTick(() => {
          if (!this.isLoading && this.paginatedItems.length > 0) {
            this.fetchCommentsForVisibleItems();
          }
        });
      }
    }
  },
  methods: {
    async fetchRequestData() {
      try {
        this.isLoading = true;
        const requestsResponse = await fetchRequestsForRole(this.getUser);

        this.requestsData = requestsResponse.map(item => ({
          ...item,
          commentsHistory: this.commentsCache[item.id] || []
        }));

        await this.fetchCommentsForVisibleItems();
        
        this.isLoading = false;
      } catch (error) {
        console.error("Error fetching data:", error);
        this.isLoading = false;
        this.handleError("Demersurile nu au putut fi încărcate. Reîncărcați pagina.");
      }
    },
    
    async fetchCommentsForVisibleItems() {
      this.checkCorrectable();
      if (this.commentLoading) return;
      
      try {
        this.commentLoading = true;
        
        const visibleItems = this.paginatedItems;
        
        const requestIdsToFetch = visibleItems
          .filter(item => item.id && !this.commentsCache[item.id])
          .map(item => item.id);
        
        if (requestIdsToFetch.length === 0) {
          this.commentLoading = false;
          return;
        }
        
        console.log(`Fetching comments for ${requestIdsToFetch.length} requests:`, requestIdsToFetch);
        
        const commentsData = await api.getBulkRequestCommentsHistory(requestIdsToFetch);
        
        Object.entries(commentsData).forEach(([requestId, comments]) => {
          const numericId = parseInt(requestId, 10);
          this.commentsCache[numericId] = comments;
          
          const item = this.requestsData.find(item => item.id === numericId);
          if (item) {
            item.commentsHistory = comments;
          }
        });
        
        console.log("Comments loaded successfully");
      } catch (error) {
        console.error("Error fetching comments:", error);
      } finally {
        this.commentLoading = false;
      }
    },
    
    handleTableOptionsUpdate(options) {
      console.log("Table options updated:", options);
      
      const sortBy = options.sortBy && options.sortBy.length ? options.sortBy : ["id"];
      const sortDesc = options.sortDesc && options.sortDesc.length ? options.sortDesc : [true];
      
      this.pagination = {
        page: options.page || 1,
        itemsPerPage: options.itemsPerPage || 10,
        sortBy: sortBy,
        sortDesc: sortDesc
      };
      
      this.sortBy = this.pagination.sortBy[0] || "id";
      this.sortDesc = this.pagination.sortDesc[0] !== false;
      
      localStorage.setItem('requestPaginationSettings', JSON.stringify({
        page: this.pagination.page,
        itemsPerPage: this.pagination.itemsPerPage,
        sortBy: this.pagination.sortBy,
        sortDesc: this.pagination.sortDesc
      }));
      
      this.$nextTick(() => {
        this.fetchCommentsForVisibleItems();
      });
    },
    
    // own rejected requests on this page: the server says whether a correction is still possible
    async checkCorrectable() {
      const me = this.getUser && this.getUser.userId;
      const ids = this.paginatedItems
        .filter((item) => item.statusId === REJECTED && item.userId === me && !(item.id in this.correctable))
        .map((item) => item.id);
      ids.forEach((id) => this.$set(this.correctable, id, false));
      await Promise.all(
        ids.map(async (id) => {
          try {
            const details = await api.getRequestDetails(id);
            this.correctable[id] = !!(details && details.actions && details.actions.correct);
          } catch (error) {
            console.error("Error checking the correction of request", id, error);
          }
        })
      );
    },
    async openCorrection(item) {
      this.openingCorrection = item.id;
      try {
        const opened = await this.$refs.correctDialog.open(item.id);
        if (!opened) this.correctable[item.id] = false;
      } finally {
        this.openingCorrection = null;
      }
    },
    cancelItem(item) {
      this.$refs.cancelDialog.show({ ...item });
    },
    openAddComment(item) {
      const what = [item.cause, item.model].filter(Boolean).join(", ");
      this.$refs.addComment.open(item.id, what ? `${what}. Demersul nu își schimbă etapa.` : "");
    },
    // a new step was written: its comment and actions must be read again
    onRequestChanged(requestId) {
      if (requestId) {
        delete this.commentsCache[requestId];
        this.$delete(this.correctable, requestId);
      }
      this.fetchRequestData();
    },
    saveStartDate(date) {
      this.startDate = date;
      this.formattedStartDate = date;
      this.$refs.startMenu.save(date);  // Explicitly close the start menu
    },
    saveEndDate(date) {
      this.endDate = date;
      this.formattedEndDate = date;
      this.$refs.endMenu.save(date);  // Explicitly close the end menu
    },
    closeDateRangePicker() {
      this.showDateRangePicker = false;
    },
    async downloadReport() {
      try {
        if (!this.startDate || !this.endDate) {
          this.handleError("Alegeți ambele date ale perioadei.");
          return;
        }
  
        const params = {
          start: this.startDate,
          end: this.endDate
        };
  
        const response = await api.downloadReport(params);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Report_${this.startDate}_${this.endDate}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
  
        this.showDateRangePicker = false;
      } catch (error) {
        console.error("Error downloading report:", error);
        this.handleError("Nu există demersuri îndeplinite în această perioadă sau raportul nu a putut fi generat.");
      }
    },
    formatCreatedAt(dateString) {
      const result = helper.formatCreatedAt(dateString);
      return result;
    },
    getTableCellValue(item, header, index) {
      if (header.value === "index") return this.rowOffset + index + 1;
      if (header.value === "user") return item.user;
      if (header.value === "status") return item.status;
      if (header.value === "equipment") return item.equipment;
      if (header.value === "model") return item.model;
      if (header.value === "registration_number")
        return item.registration_number;
      if (header.value === "cause") return item.cause;
      if (header.value === "userDepartment") return item.userDepartment;
      if (
        header.value === "service_price" &&
        (this.isAdmin || this.isEngineer || this.isDtiSef || this.isRector)
      ) {
        return item.service_price;
      }
      if (header.value === "date") return this.formatCreatedAt(item.date);
      if (header.value === "actions") return "";
      return "";
    },
    sortTable(header) {
      const result = helper.sortTable(header);
      return result;
    },
    updateVisibleColumns() {
      if (this.isAdmin || this.isEngineer || this.isDtiSef || this.isRector) {
        this.visibleHeaders = this.headers
          .filter((header) => header.visible)
          .map(header => ({...header, sortable: header.value !== 'actions' && header.value !== 'comments'}));
      } else {
        this.visibleHeaders = this.headers
          .filter((header) => header.visible && header.notForUser)
          .map(header => ({...header, sortable: header.value !== 'actions' && header.value !== 'comments'}));
      }
    },
    handleError(message) {
      this.errorMessage = message;
    },
    handleSuccess(message) {
      this.successMessage = message;
    },
    handleSearch(searchTerm) {
      this.search = searchTerm;
    },
    clearMessage() {
      this.errorMessage = "";
      this.successMessage = "";
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    statusColor(status) {
      return statusColor(status);
    },
  },
  computed: {
    ...mapGetters(["getUser"]),
    isAdmin() {
      return this.$store.getters.getUser.role === "admin";
    },
    isEngineer() {
      return this.$store.getters.getUser.role === "inginer";
    },
    isEngineerPC(){
      return this.$store.getters.getUser.role === "inginer-pc";
    },
    isDtiSef() {
      return this.$store.getters.getUser.role === "dtisef";
    },
    isContability() {
      return this.$store.getters.getUser.role === "contability";
    },
    isRector() {
      return this.$store.getters.getUser.role === "rector";
    },
    myDepartments() {
      return this.$store.getters.myDepartments;
    },
    // + the sub-units without a Șef whose requests a Șef approves
    requestDepartments() {
      return this.$store.getters.requestDepartments;
    },
    subtitle() {
      if (this.isAdmin || this.isDtiSef || this.isRector || this.isEngineer || this.isEngineerPC) {
        return "Toate cererile din universitate, cu starea și comentariile lor.";
      }
      // + the ones they filed, decided on, or that concern their items (server readerSees)
      return this.myDepartments.length > 1
        ? `Cererile celor ${this.myDepartments.length} departamente ale dvs. și cele care vă privesc.`
        : "Cererile departamentului dvs. și cele care vă privesc, cu starea și comentariile lor.";
    },
    filteredData() {
      const rows = this.department
        ? this.requestsData.filter((item) => item.userDepartment === this.department)
        : this.requestsData;
      return helper.filteredData(rows, this.search);
    },
    // "Nr" continues on the next pages; itemsPerPage is -1 for "Toate"
    rowOffset() {
      const { page, itemsPerPage } = this.pagination;
      return itemsPerPage > 0 ? (page - 1) * itemsPerPage : 0;
    },
    paginatedItems() {
      let sortedData = [...this.filteredData];
      
      if (this.pagination.sortBy && this.pagination.sortBy.length > 0) {
        const sortKey = this.pagination.sortBy[0];
        const sortDesc = this.pagination.sortDesc[0];
        
        sortedData.sort((a, b) => {
          let aValue, bValue;
          
          if (sortKey === 'date') {
            aValue = new Date(a.date || a.created_at).getTime();
            bValue = new Date(b.date || b.created_at).getTime();
          } else if (sortKey === 'id') {
            aValue = parseInt(a.id);
            bValue = parseInt(b.id);
          } else {
            aValue = a[sortKey];
            bValue = b[sortKey];
          }
          
          if (aValue < bValue) return sortDesc ? 1 : -1;
          if (aValue > bValue) return sortDesc ? -1 : 1;
          return 0;
        });
      }
      
      if (this.pagination.itemsPerPage <= 0) return sortedData;
      return sortedData.slice(this.rowOffset, this.rowOffset + this.pagination.itemsPerPage);
    }
  },
  mounted() {
    // only an admin acts on a row (Anulează); the author's correction sits under the status
    this.headers = require("./requests.json")
      .filter(header => header.value !== 'actions' || this.isAdmin)
      .map(header => ({
        ...header,
        sortable: header.value !== 'actions' && header.value !== 'comments'
      }));
    if (!(this.isAdmin || this.isEngineer || this.isDtiSef || this.isRector)) {
      this.visibleHeaders = this.headers.filter(
        (header) => header.defaultVisible && header.notForUser 
      );
      this.headers = this.headers.filter(
        (header) => header.defaultVisible && header.notForUser
      );
    } else {
      this.visibleHeaders = this.headers.filter(
        (header) => header.defaultVisible
      );
    }
    this.headers.forEach((header) => {
      if (this.isAdmin || this.isEngineer || this.isDtiSef || this.isRector) {
        return (header.visible = header.defaultVisible && header.notForUser);
      } else {
        return (header.visible = header.defaultVisible && header.notForUser);
      }
    });
    
    this.$refs.dialogForm.$on("error", this.handleError);
    this.$refs.dialogForm.$on("success", this.handleSuccess);
    
    this.$watch('$vuetify.breakpoint.name', () => {
      this.$nextTick(() => {
        this.fetchCommentsForVisibleItems();
      });
    });
    
    const savedPaginationSettings = localStorage.getItem('requestPaginationSettings');
    if (savedPaginationSettings) {
      try {
        const settings = JSON.parse(savedPaginationSettings);
        this.pagination = {
          page: settings.page || 1,
          itemsPerPage: settings.itemsPerPage || 10,
          sortBy: settings.sortBy || ["id"],
          sortDesc: settings.sortDesc || [true]
        };
        this.sortBy = this.pagination.sortBy[0] || "id";
        this.sortDesc = this.pagination.sortDesc[0] !== false;
      } catch (e) {
        console.error('Error parsing saved pagination settings:', e);
      }
    }
    
    this.$nextTick(() => {
      this.fetchRequestData();
      // "Demers nou" from the home page
      if (this.$route.query.nou) {
        this.$refs.dialogForm.open();
        this.$router.replace({ query: {} });
      }
    });
    
    window.addEventListener('beforeunload', () => {
      localStorage.setItem('requestPaginationSettings', JSON.stringify({
        page: this.pagination.page,
        itemsPerPage: this.pagination.itemsPerPage,
        sortBy: this.pagination.sortBy,
        sortDesc: this.pagination.sortDesc
      }));
    });
  },
};
</script>

<style scoped>
.report-hint {
  color: var(--ink-2);
  font-size: 1rem;
  margin-bottom: 18px;
}
/* the default columns fit a 1400px screen without scrolling sideways: tighter cells, a narrow dot rail */
.requests ::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th,
.requests ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: 0 10px;
}
.comments-wrapper {
  max-height: 160px;
  overflow-y: auto;
  padding: 4px 0;
}
.compact-timeline {
  padding-top: 0;
}
.compact-timeline.v-timeline--dense::before {
  left: 11px !important;
}
.compact-timeline ::v-deep .v-timeline-item__divider {
  min-width: 24px;
}
.compact-timeline ::v-deep .v-timeline-item__body {
  max-width: calc(100% - 24px);
  padding-left: 8px;
}
.compact-item {
  padding-bottom: 8px !important;
}
.comment-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 0;
  font-size: 0.92rem;
}
.user-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}
.comment-body {
  font-size: 0.92rem;
  color: var(--ink-2);
  margin: 2px 0;
  white-space: pre-line;
  word-break: break-word;
}
.comment-date {
  font-size: 0.8rem;
  color: var(--ink-3);
}
.no-comments {
  color: var(--ink-3);
}
.comment-photos {
  margin: 4px 0 2px;
}
.add-comment-btn {
  margin: 4px 0 0 -8px;
}
.correct-btn {
  display: flex;
  margin-top: 8px;
}
/* a cancelled request stays in the list, muted */
tr.row-cancelled > td {
  color: var(--ink-3);
}
tr.row-cancelled > td:first-child {
  box-shadow: inset 4px 0 0 var(--slate);
}
</style>
